import {
  Directive,
  ElementRef,
  Output,
  EventEmitter,
  HostListener,
  Input,
  inject,
} from '@angular/core';

@Directive({
  selector: '[interfaceClickOnOutside]',
  standalone: true,
})
export class ClickOnOutsideDirective {
  private _elementRef = inject(ElementRef);

  /**
   * Elements or selectors that should prevent the outside click event from firing.
   * Accepts an array of ElementRef, HTMLElement, or CSS selector strings.
   */
  @Input() clickOnOutsideExclude: (ElementRef | HTMLElement | string)[] = [];

  @Output()
  public clickOutside = new EventEmitter<MouseEvent>();

  /**
   * Handles the document mousedown event and determines if a click occurred outside the host element.
   *
   * If the click is not within the host element or any excluded elements/selectors,
   * emits the `clickOutside` event.
   *
   * @param {MouseEvent} event - The mouse event object.
   */
  @HostListener('document:mousedown', ['$event'])
  public onClick(event: MouseEvent): void {
    const targetElement = event.target;
    if (!(targetElement instanceof HTMLElement)) return;

    // Check if the click is inside the host element
    if (this._elementRef.nativeElement.contains(targetElement)) return;

    // Check if the click is inside any excluded element or matches any selector
    if (this.clickedOnExcluded(targetElement)) return;

    // Emit the clickOutside event if none of the conditions above are met
    this.clickOutside.emit(event);
  }

  /**
   * Checks if the click target is within any excluded element or matches any excluded selector.
   *
   * @param {HTMLElement} targetElement - The element that was clicked.
   * @returns {boolean} True if the target is within an excluded element or matches a selector; otherwise, false.
   */
  private clickedOnExcluded(targetElement: HTMLElement): boolean {
    for (const item of this.clickOnOutsideExclude) {
      if (this.checkElementRef(item, targetElement)) return true;
      if (this.checkHTMLElement(item, targetElement)) return true;
      if (this.checkSelector(item, targetElement)) return true;
    }
    return false;
  }

  /**
   * Checks if the provided item is an ElementRef and contains the target element.
   *
   * @param {any} item - The item to check.
   * @param {HTMLElement} targetElement - The element that was clicked.
   * @returns {boolean} True if the item is an ElementRef containing the target element.
   */
  private checkElementRef(item: any, targetElement: HTMLElement): boolean {
    if (item && item.nativeElement instanceof HTMLElement) {
      return item.nativeElement.contains(targetElement);
    }
    return false;
  }

  /**
   * Checks if the provided item is an HTMLElement and contains the target element.
   *
   * @param {any} item - The item to check.
   * @param {HTMLElement} targetElement - The element that was clicked.
   * @returns {boolean} True if the item is an HTMLElement containing the target element.
   */
  private checkHTMLElement(item: any, targetElement: HTMLElement): boolean {
    if (item instanceof HTMLElement) {
      return item.contains(targetElement);
    }
    return false;
  }

  /**
   * Checks if the provided item is a selector string and the target element matches it or is inside it.
   *
   * @param {any} item - The item to check.
   * @param {HTMLElement} targetElement - The element that was clicked.
   * @returns {boolean} True if the target element matches or is inside the selector.
   */
  private checkSelector(item: any, targetElement: HTMLElement): boolean {
    if (typeof item === 'string' && item.trim() !== '') {
      return !!targetElement.closest(item);
    }
    return false;
  }
}
